import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { HiArrowLeft } from 'react-icons/hi';

export default function OwnerLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, user, logout } = useAuth();
  const navigate = useNavigate();

  // After successful login, check the role using the roles array
  useEffect(() => {
  if (!user) return;
  if (user.role === 'ADMIN') {
    navigate('/admin');
  } else {
    setError('Access denied. Only the store owner can log in here.');
    logout();
    setLoading(false);
  }
}, [user, navigate, logout]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      // The useEffect above will handle the redirect
    } catch (err) {
      setError('Invalid credentials or account not verified. Please check your email.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row">
      {/* Left – Brand Section */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 bg-white relative items-center justify-center min-h-screen border-r border-gray-100">
        <div className="text-center px-12 max-w-lg">
          <Link to="/" className="inline-block">
            <img
              src="/images/DarklogoAuth.png"
              alt="WAVEYYENT"
              className="w-[250px] sm:w-[300px] md:w-[350px] mx-auto hover:scale-105 transition-transform duration-500"
            />
          </Link>
        </div>
      </div>

      {/* Right – Form Section */}
      <div className="w-full lg:w-7/12 xl:w-1/2 flex items-center justify-center px-6 sm:px-12 lg:px-16 py-8 lg:py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <Link to="/">
              <img
                src="/images/DarklogoAuth.png"
                alt="WAVEYYENT"
                className="w-[180px] sm:w-[220px] mx-auto"
              />
            </Link>
          </div>

          <div className="mb-8 lg:mb-10">
            <h2 className="text-3xl lg:text-4xl font-bold text-black mb-3 tracking-tight">
              WELCOME BACK WAVEY
            </h2>
            <p className="text-gray-500 text-sm">
              Enter your credentials to manage the store.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 lg:py-3.5 bg-gray-50 border-2 rounded-2xl text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300"
                placeholder="you@email.com"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 lg:py-3.5 bg-gray-50 border-2 rounded-2xl text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-4 rounded-2xl font-semibold text-sm hover:bg-gray-900 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/20"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in…
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 lg:mt-8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-black transition"
            >
              <HiArrowLeft size={16} /> Back to store
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}